import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { useOs2, PageHead, Card, SEVERITY, timeAgo, Fade, LoadingState, Pager, RangeNotice } from './os2ui';

/* Notifications — the CEO inbox. "What needs my attention right now?" */

const FILTERS = [
  ['all', 'All', 'Todas'],
  ['success', 'Wins', 'Logros'],
  ['warning', 'Warnings', 'Avisos'],
  ['error', 'Errors', 'Errores'],
  ['info', 'Info', 'Info'],
];

export default function NotificationsOsPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/notifications');
  const [f, setF] = useState('all');
  const [page, setPage] = useState(1);
  const [resolving, setResolving] = useState('');
  const PER_PAGE = 8;

  const resolve = async (nid) => {
    setResolving(nid);
    try {
      const r = await authFetch('/os2/notifications/resolve', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nid }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(isEs ? 'Notificación resuelta ✓' : 'Notification resolved ✓');
      reload();
    } catch (e) {
      toast.error(String(e.message));
    } finally {
      setResolving('');
    }
  };

  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <p className="text-sm text-gray-400">{isEs ? 'Cargando…' : 'Loading…'}</p>;

  const all = (d.notifications || []).filter((n) => f === 'all' || n.severity === f);
  const pages = Math.max(1, Math.ceil(all.length / PER_PAGE));
  const curPage = Math.min(page, pages);
  const items = all.slice((curPage - 1) * PER_PAGE, curPage * PER_PAGE);
  const groups = {};
  items.forEach((n) => {
    const day = (n.at || '').slice(0, 10);
    (groups[day] = groups[day] || []).push(n);
  });
  const dayLabel = (day) => {
    const today = new Date().toISOString().slice(0, 10);
    const yest = new Date(Date.now() - 864e5).toISOString().slice(0, 10);
    if (day === today) return isEs ? 'Hoy' : 'Today';
    if (day === yest) return isEs ? 'Ayer' : 'Yesterday';
    return day;
  };

  return (
    <div data-testid="os2-notifications">
      <PageHead
        title={isEs ? 'Notificaciones' : 'Notifications'}
        question={isEs ? '¿Qué necesita mi atención ahora mismo?' : 'What needs my attention right now?'}
        fresh={{ at: fetchedAt, onReload: reload, loading, isEs }}
        module="AI OS"
        right={
          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTERS.map(([k, en, es]) => (
              <button key={k} onClick={() => { setF(k); setPage(1); }}
                className={`h-8 px-3 rounded-full border text-[12px] font-semibold transition-colors ${
                  f === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
                data-testid={`notif-filter-${k}`}>
                {isEs ? es : en}
              </button>
            ))}
          </div>
        }
      />
      <div className="-mt-3 mb-4"><RangeNotice isEs={isEs} windows={isEs ? 'cronología completa de avisos recientes' : 'full recent-alerts timeline'} /></div>

      {Object.entries(groups).map(([day, list], gi) => (
        <Fade key={day} delay={gi * 0.06}>
          <p className="mt-5 mb-2 text-[11px] tracking-[0.18em] uppercase text-gray-400 font-bold">{dayLabel(day)}</p>
          <Card className="divide-y divide-gray-100 overflow-hidden">
            {list.map((n, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/60 transition-colors" data-testid={`notif-item-${n.type}-${i}`}>
                <span className={`w-9 h-9 shrink-0 rounded-xl border flex items-center justify-center text-[17px] ${SEVERITY[n.severity]}`}>{n.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-semibold text-gray-900">{isEs ? n.title_es : n.title_en}</p>
                  <p className="text-[12.5px] text-gray-500 truncate">
                    <span className="inline-block rounded bg-gray-100 text-gray-500 px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide mr-1.5 align-middle">{(n.type || '').replaceAll('_', ' ')}</span>
                    {n.detail}
                  </p>
                </div>
                <div className="text-right shrink-0 flex items-center gap-2">
                  <div>
                    <p className="text-[11px] text-gray-300 tabular-nums">{timeAgo(n.at, isEs)}</p>
                    {n.source && <p className="text-[10.5px] text-gray-400 capitalize">{n.source}</p>}
                  </div>
                  <button onClick={() => resolve(n.nid)} disabled={resolving === n.nid}
                    title={isEs ? 'Marcar como resuelta' : 'Mark as resolved'}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-300 hover:text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 transition-colors disabled:opacity-40"
                    data-testid={`notif-resolve-${n.type}-${i}`}>
                    <Check className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </Card>
        </Fade>
      ))}
      {!items.length && (
        <Card className="p-12 text-center border-dashed"><p className="text-sm text-gray-400">{isEs ? 'Sin notificaciones en este filtro' : 'No notifications in this filter'}</p></Card>
      )}
      <Pager page={curPage} pages={pages} setPage={setPage} testid="notif-pager" />
    </div>
  );
}
